import { Dialog } from "@components/ui/Dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lightbulb } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { API_ENDPOINTS } from "../../config/constants";
import { useTicketTransition } from "../../hooks/useTicketMutations";

const proposeSchema = z.object({
  solutionDescription: z
    .string()
    .min(10, "La description doit contenir au moins 10 caractères")
    .max(5000, "5000 caractères max"),
});

type ProposeFormValues = z.infer<typeof proposeSchema>;

interface ProposeSolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: number | string;
}

export function ProposeSolutionModal({ isOpen, onClose, ticketId }: ProposeSolutionModalProps) {
  const transition = useTicketTransition();
  const form = useForm<ProposeFormValues>({
    resolver: zodResolver(proposeSchema),
    defaultValues: { solutionDescription: "" },
  });

  const onSubmit = (values: ProposeFormValues) => {
    transition.mutate(
      {
        id: ticketId,
        endpoint: API_ENDPOINTS.TICKETS.PROPOSE_SOLUTION(ticketId),
        data: { solutionDescription: values.solutionDescription },
      },
      {
        onSuccess: () => {
          form.reset();
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title="Proposer une solution"
      icon={<Lightbulb className="w-5 h-5 text-info" />}
      size="md"
      footer={
        <div className="flex gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-info gap-1.5"
            disabled={transition.isPending || !form.formState.isValid}
          >
            {transition.isPending ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Lightbulb className="w-3.5 h-3.5" />
            )}
            Proposer
          </button>
        </div>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Description de la solution</span>
          </label>
          <textarea
            className="textarea textarea-bordered w-full min-h-[120px]"
            placeholder="Décrivez la solution proposée..."
            {...form.register("solutionDescription")}
          />
          {form.formState.errors.solutionDescription && (
            <label className="label">
              <span className="label-text-alt text-error">
                {form.formState.errors.solutionDescription.message}
              </span>
            </label>
          )}
        </div>
      </form>
    </Dialog>
  );
}
